/**
 * ListenerUtils
 *
 * @module lib/ListenerUtils
 */
import { List, Map } from 'immutable';
import isNil from 'lodash/isNil';
import memoize from 'lodash/memoize';
// @ts-expect-error TS(2305) FIXME: Module '"redux-form"' has no exported member 'getV... Remove this comment to see the full error message
import { getValues } from 'redux-form';
import { createSelector } from 'reselect';

import { type State, type ValueOf } from 'src/types';

import ListenerPlaceholder from '../../static/images/empty_profile_listener.png';
import * as Constants from '../constants';
import { ANON_TIMEOUT, AppConstants } from '../constants/App';
import { AudioFormats } from '../constants/AudioPlayer';
import { TREATMENT_ARMS } from '../constants/Experiments';
import { FacebookConstants } from '../constants/Facebook';
import { FEATURES } from '../constants/FeatureFlags';
import { ListenerKeys } from '../constants/Listener';
import { LoginKeys } from '../constants/Login';
import { SmartConversionKeys } from '../constants/SmartConversion';
import { SourceTypes } from '../constants/SourceTypes';
import { getInDomain } from '../domains/externalUtils';
import * as CapabilitiesUtils from '../lib/CapabilitiesUtils';
import * as TunerVarUtils from '../lib/configuration/TunerVarUtils';
import { hex_md5 } from '../lib/md5';

import { setDatadogContext, setDatadogTag, setDatadogUser } from './DatadogClient';
import { getNow } from './TimeUtils';

/**
 * Checks state to see if a listener has ever logged in to the pandora app
 *
 * @param {object} state - store state
 * @returns {boolean} has a listener ever logged in to the pandora app?
 */
export const hasLoggedIn = (state: State): boolean => {
  return state.login.get(LoginKeys.HAS_LOGGED_IN);
};

/**
 * Checks state to see if we have a logged in user
 *
 * @param {object} state - store state
 * @returns {boolean} is the current user logged in?
 */
export const isLoggedIn = (state: State): boolean => {
  return state.login.get(LoginKeys.IS_LOGGED_IN_KEY);
};

export const isRememberMe = (state: State): boolean => {
  return state.login.get(LoginKeys.LOGIN_REMEMBER_ME_KEY);
};

export const wasAuthUserInitiated = (state: State): boolean => {
  return !!state.login.get(LoginKeys.LOGIN_USER_INITIATED);
};

/**
 * Checks state to see if we have a login call in flight
 *
 * @param {object} state - store state
 * @returns {boolean} is the current user attempting to log in
 */
export const isLoggingIn = (state: State): boolean => {
  return state.login.get(LoginKeys.IS_LOGGING_IN_KEY);
};

/**
 * Checks state to see if we have an Authenticated user
 *
 * @param {object} state - store state
 * @returns {boolean} is the current user authenticated?
 */
export const isAuthenticated = (state: State): boolean => {
  return !!state.login.get(LoginKeys.LOGIN_AUTH_TOKEN);
};

export const getJustMigratedWindowsAccount = (state: State): $TSFixMe => {
  return state.login.get(LoginKeys.JUST_MIGRATED_WINDOWS_ACCOUNT);
};

export const getListeners = (state: State): $TSFixMe => {
  return state.entities.getIn(['listeners', ListenerKeys.LISTENERS_KEY]);
};

export const getDefaultResetPasswordUsername = (state: State): $TSFixMe => {
  return state.login.get(LoginKeys.DEFAULT_RESET_PASSWORD_USERNAME);
};
/**
 * Get listener map object from unique id
 *
 * @param {object} state - redux state
 * @param {string} id - unique listener id
 * @returns {object | null} - current listener
 */
export const getListenerFromId = (state: State, id: $TSFixMe): $TSFixMe => {
  const listeners = getListeners(state);
  return listeners ? listeners.get(id) : null;
};

/*
 * return Auth Token for the current listener
 * @param {Object} state - store state
 * @returns {String} auth Token
 */
export const getAuthToken = (state: State): string => {
  return state.login.get(LoginKeys.LOGIN_AUTH_TOKEN);
};

export const getCurrentListenerIdFromState = (state: State): string => {
  return state.entities.getIn(['listeners', ListenerKeys.CURRENT_USER_ID_KEY]);
};

/**
 * Checks state for current listener id, corresponding the related entity
 *
 * @param {object} state - store state
 * @returns {object | null} the logged in listener or null if none.
 */
export const getCurrentListenerFromState = (state: State): $TSFixMe => {
  const listenerId = getCurrentListenerIdFromState(state);
  return listenerId ? getListenerFromId(state, listenerId) : null;
};

export const getCurrentListenerTokenFromState = (state: State): $TSFixMe => {
  const listener = getCurrentListenerFromState(state);
  return listener ? listener.get('listenerToken') : null;
};

export const getCurrentListenerKruxTokenFromState = (state: State): string => {
  const listener = getCurrentListenerFromState(state);
  return listener ? listener.get('kruxToken') : null;
};

export const getListenerPublicLid = createSelector(getCurrentListenerFromState, listener =>
  listener ? listener.get('publicLid') : '',
);

/**
 * Gets current listener twitter accessToken
 *
 * @param {object} state - store state
 * @returns {string | null} - listener twitter acecessToken or null if none
 */
export const getListenerTwitterAccessToken = (state: State): string | null => {
  const listener = getCurrentListenerFromState(state);
  return listener ? listener.getIn(['twitterData', 'token']) : null;
};

/**
 * Gets current listener twitter access token secret
 *
 * @param {object} state - store state
 * @returns {string | null} - listener twitter acecessToken secret or null if none
 */
export const getListenerTwitterAccessTokenSecret = (state: State): string | null => {
  const listener = getCurrentListenerFromState(state);
  return listener ? listener.getIn(['twitterData', 'secret']) : null;
};

/**
 * Get user name of current user
 *
 * @param {object} state - redux state
 * @returns {string | null} - current username
 */
export const getCurrentUsername = (state: State): string | null => {
  return state.login.get(LoginKeys.USERNAME);
};

/**
 * Get web name of current user
 *
 * @param {object} state - redux state
 * @returns {string | null} - current user's webname
 */
export const getCurrentUserWebName = (state: State): string | null => {
  const listener = getCurrentListenerFromState(state);
  return listener ? listener.get('webname') : null;
};

/**
 * Get zip code of current user
 *
 * @param {object} state - redux state
 * @returns {string | null} - current user's zipcode
 */
export const getCurrentUserZipCode = (state: State): string | null => {
  const listener = getCurrentListenerFromState(state);
  return listener ? listener.get('zipCode') : null;
};

export const getCurrentUserBirthYear = (state: State): number => {
  const listener = getCurrentListenerFromState(state);
  return listener ? listener.get('birthYear') : 0;
};

export const getCurrentUserGender = (state: State): string => {
  const listener = getCurrentListenerFromState(state);
  return listener ? listener.get('gender') : '';
};

/**
 * Returns whether is the current listener is anon. returns true if
 * no listener in the store at this point
 *
 * @param {object} state - redux state
 * @returns {boolean} - anon?
 */
export const isAnonymousListener = (state: State): boolean => {
  const listener = getCurrentListenerFromState(state);
  return listener ? listener.get('isAnonymous') : true;
};

/**
 * Returns whether is the current listener is new. returns true if
 * no listener in the store at this point
 *
 * @param {object} state - redux state
 * @returns {boolean} - isNew?
 */
export const isNewListener = (state: State): boolean => {
  const listener = getCurrentListenerFromState(state);
  return listener ? listener.get('isNew') : true;
};

/**
 * Returns whether is the current listener has given positive feedback on any track
 *
 * @param {object} state - redux state
 * @returns {boolean} - hasGivenPositiveFeedback?
 */
export const hasGivenPositiveFeedback = (state: State): boolean => {
  const listener = getCurrentListenerFromState(state);
  return (listener && listener.has('hasGivenPositiveFeedback')) || false;
};

/**
 * Returns whether is the current listener has given negative feedback on any track
 *
 * @param {object} state - redux state
 * @returns {boolean} - hasGivenNegativeFeedback?
 */
export const hasGivenNegativeFeedback = (state: State): boolean => {
  const listener = getCurrentListenerFromState(state);
  return (listener && listener.has('hasGivenNegativeFeedback')) || false;
};

/**
 * Get profile image associated with passed in listener
 *
 * @param {Map} listener - listener object
 * @returns {string | null} - listener image url or null if there is none
 */

export const getListenerPlaceholder = (listener: $TSFixMe): string | null => {
  if (listener && listener.get('placeholderProfileImageUrl')) {
    return listener.get('placeholderProfileImageUrl');
  }
  return ListenerPlaceholder;
};

export const getListenerImageUrl = (listener: $TSFixMe): string | null => {
  const facebookId = listener && listener.getIn(['facebookData', 'facebookId'], null);
  const accessToken = listener && listener.getIn(['facebookData', 'accessToken'], null);
  if (facebookId && accessToken) {
    return `https://graph.facebook.com/${FacebookConstants.VERSION}/${facebookId}/picture?type=large&access_token=${accessToken}`;
  }
  return getListenerPlaceholder(listener);
};

// UNUSED
export const getFacebookTokenValidity = (listener: $TSFixMe): $TSFixMe => {
  if (!listener) return false;
  const validity = listener.get('IS_FACEBOOK_TOKEN_VALID');
  return !isNil(validity) ? validity : false;
};

export const isConnectedToFacebook = (state: State): boolean => {
  const listener = getCurrentListenerFromState(state);
  if (listener) {
    // Since `facebookData` is generated from backend, if `facebookData` is available,
    // it means this user has connected to Facebook before but the authorization can be expired or revoked.
    // (the backend only removes `facebookData` if users have disconnected explicitly from our mobile or web apps)
    const facebookData = listener.get('facebookData');
    return !!facebookData;
  }
  return false;
};

export const getListenerIsConnectedToTwitter = (listener: $TSFixMe): boolean | null => {
  if (listener) {
    const twitterData = listener.get('twitterData');
    return twitterData && !!twitterData.get('token');
  }
  return null;
};

/**
 * Check if the listener has full profile loaded from listener/getProfile
 * Calling that method merges additional data into listener entity
 * This method uses stationCount as the indicator
 *
 * @param {Map} listener - listener object
 * @returns {boolean} - true if listener has full profile; false otherwise
 */
export const isFullProfile = (listener: $TSFixMe): boolean => {
  return listener.get('stationCount') !== undefined;
};

export const getGenderFirstChar = (listener: $TSFixMe): string => {
  const gender = (listener && listener.get('gender')) || '';
  return gender.charAt(0);
};

export const getThumbedTracks = (state: State): $TSFixMe => {
  return state.feedback.get(Constants.Feedback.Keys.ALL_THUMBED_TRACKS);
};

/**
 * @param  {object} state - the redux store state
 * @returns {Map} - an immutable Map of the Feedback loaded into the app
 */
export const getThumbedUpTracks = (state: State): $TSFixMe => {
  const allThumbs = state.feedback.get(Constants.Feedback.Keys.ALL_THUMBED_TRACKS);
  const thumbedUpTracks = allThumbs.filter((thumb: $TSFixMe) => thumb.get('isPositive') === true);
  return thumbedUpTracks;
};

export const getFeedbackByFeedbackId = (state: State, id: $TSFixMe): $TSFixMe => {
  const thumbedTracks = getThumbedTracks(state);
  const feedback = thumbedTracks.get(id);
  return feedback || null;
};

export const getThumbedUpFeedbackByFeedbackId = (state: State, id: string): $TSFixMe => {
  const thumbedUpTracks = getThumbedUpTracks(state);
  const feedback = thumbedUpTracks.get(id);
  return feedback || null;
};

export const hasFetchedFeedbackData = (state: State): $TSFixMe => {
  return state.feedback.get(Constants.Feedback.Keys.FETCHED_DATA_SUCCESS_KEY);
};

export const getListenerConfigById = (state: State, id: $TSFixMe): $TSFixMe => {
  const listener = getListenerFromId(state, id);
  return listener ? listener.get('config') || Map() : Map();
};

export const getCurrentListenerConfig = (state: State): $TSFixMe => {
  return getListenerConfigById(state, getCurrentListenerIdFromState(state));
};

/**
 * Retrieves the value stored for this listeners branding tier
 *
 * @param  {object} state - store state
 * @returns {string} the value stored for branding tier i.e (Pandora, PandoraPlus, etc)
 */
export const getBranding = (state: State): string => {
  return getCurrentListenerConfig(state).get(ListenerKeys.BRANDING);
};

/**
 * Use feature flag detection instead
 * If tier detection is absolutely needed for subscription/billing use productTierIsAdSupported() in src/domains/listener/utils
 *
 * @deprecated
 * @param  {object} state - store state
 * @returns {boolean}
 */
export const listenerIsBasic = (state: State): boolean => {
  return getBranding(state) === AppConstants.BRANDING_PANDORA;
};

/**
 * Use feature flag detection instead
 * If tier detection is absolutely needed for subscription/billing use productTierIsPlus() in src/domains/listener/utils
 *
 * @deprecated
 * @param  {object} state - store state
 * @returns {boolean}
 */
export const listenerIsPlus = (state: State): boolean => {
  return getBranding(state) === AppConstants.BRANDING_PANDORA_PLUS;
};

/**
 * Use feature flag detection instead
 * If tier detection is absolutely needed for subscription/billing use productTierIsPremium() in src/domains/listener/utils
 *
 * @deprecated
 * @param  {object} state - store state
 * @returns {boolean}
 */
export const listenerIsPremium = (state: State): boolean => {
  return getBranding(state) === AppConstants.BRANDING_PANDORA_PREMIUM;
};

export const listenerIsBusiness = (state: State): boolean => {
  return getBranding(state) === AppConstants.BRANDING_BUSINESS;
};

/**
 * Retrieves the value stored for this listeners daily skip limit
 *
 * @param  {object} state - store state
 * @returns {number} the number of skips this user may skip in a day
 */
export const getDailySkipLimit = (state: State): $TSFixMe => {
  return getCurrentListenerConfig(state).get(ListenerKeys.DAILY_SKIP_LIMIT);
};

/**
 * Retrieves the value stored for this listeners station skip limit
 *
 * @param  {object} state - store state
 * @returns {number} the number of skips for this station in an hour
 */
export const getStationSkipLimit = (state: State): number => {
  return getCurrentListenerConfig(state).get(ListenerKeys.STATION_SKIP_LIMIT);
};

/*
 * FEATURE FLAGS
 */

const emptyMap = Map();

export const getOverrideFactory = (debugKey: $TSFixMe) => {
  return function (state: State): $TSFixMe {
    if (!state || !state.debug || !state.debug.getIn) return emptyMap;
    return state.debug.getIn(['flagOverrides', debugKey], emptyMap);
  };
};
export const getExperimentOverrides = getOverrideFactory('experiments');
export const getFeatureOverrides = getOverrideFactory('features');

export const getRawFlagListFactory = (listenerKey: $TSFixMe): $TSFixMe => {
  return createSelector(getCurrentListenerConfig, listener => {
    if (!listener || !listener.get) return List();
    return listener.get(listenerKey);
  });
};
export const getRawUserFeaturesList = getRawFlagListFactory(ListenerKeys.FLAGS);
export const getRawUserExperimentsList = getRawFlagListFactory(ListenerKeys.EXPERIMENTS);

export const getRawFlagMapFactory = (getRawList: $TSFixMe, fullFlagList: $TSFixMe): $TSFixMe => {
  return createSelector(getRawList, rawList => {
    let flagMap = Map();
    fullFlagList &&
      fullFlagList.forEach((flag: $TSFixMe) => {
        flagMap = flagMap.set(flag, false);
      });
    rawList &&
      (rawList as $TSFixMe).forEach((flag: $TSFixMe) => {
        flagMap = flagMap.set(flag, true);
      });
    return flagMap;
  });
};
const allFeatures = Map(FEATURES).flip().toList();
export const getRawUserFeaturesMap = getRawFlagMapFactory(getRawUserFeaturesList, allFeatures);
const allExperiments = Map(TREATMENT_ARMS).toList();
export const getRawUserExperimentsMap = getRawFlagMapFactory(
  getRawUserExperimentsList,
  allExperiments,
);

export const getFlagMapFactory = (getRaw: $TSFixMe, getOverride: $TSFixMe): $TSFixMe => {
  // @ts-expect-error TS(2345) FIXME: Argument of type 'any[]' is not assignable to para... Remove this comment to see the full error message
  return createSelector([getRaw, getOverride], (rawMap: $TSFixMe, overrideMap: $TSFixMe) => {
    let finalFlagMap = rawMap;
    overrideMap.forEach((val: $TSFixMe, feature: $TSFixMe) => {
      finalFlagMap = finalFlagMap.set(feature, !!val);
    });
    return finalFlagMap;
  });
};
export const getUserFeaturesMap = getFlagMapFactory(getRawUserFeaturesMap, getFeatureOverrides);
export const getUserExperimentsMap = getFlagMapFactory(
  getRawUserExperimentsMap,
  getExperimentOverrides,
);

export const getFlagFactory = (getMap: $TSFixMe): $TSFixMe => {
  return createSelector(getMap, flagMap => {
    let finalFlagList = List();
    (flagMap as $TSFixMe).forEach((value: $TSFixMe, key: $TSFixMe) => {
      finalFlagList = value ? finalFlagList.push(key) : finalFlagList;
    });
    return finalFlagList;
  });
};
export const getUserFeatures = getFlagFactory(getUserFeaturesMap);
export const getUserExperiments = getFlagFactory(getUserExperimentsMap);

export const isFlagEnabledFactory = (
  getFlags: $TSFixMe,
): ((state: State, flag: $TSFixMe) => boolean) => {
  return function (state: State, flag: $TSFixMe): boolean {
    const flagList = getFlags(state);
    return flagList?.contains(flag) || false;
  };
};
export const isFeatureEnabled = isFlagEnabledFactory(getUserFeatures);

export const isExperimentEnabled = isFlagEnabledFactory(getUserExperiments);

export const doesUserHaveUnlimitedReplays = (state: State): boolean => {
  return isFeatureEnabled(state, 'adFreeReplay');
};

export const doesUserHaveAdSupportedReplays = (state: State): boolean => {
  return isFeatureEnabled(state, 'adSupportedReplay');
};

export const canUserReplay = (state: State): boolean => {
  return doesUserHaveAdSupportedReplays(state) || doesUserHaveUnlimitedReplays(state);
};

export const registeredOnRedesign = (state: State): boolean => {
  return isFeatureEnabled(state, FEATURES.registeredOnRedesign);
};

export const isListenerAdvertiser = (state: State): boolean => {
  return isFeatureEnabled(state, FEATURES.isAdvertiser);
};

export const hasPremiumFeatures = (state: State): boolean => {
  return isFeatureEnabled(state, FEATURES.onDemand);
};

export const isStationPlayback = (state: State): boolean => {
  return getInDomain('playback.utils.getCurrentPlaylistType', null)(state) === SourceTypes.STATION;
};

export const showPremiumAccess = (state: State): boolean => {
  return !hasPremiumFeatures(state);
};

export const shouldCurrentListenerGetAds = (state: State): boolean => {
  return (
    !isFeatureEnabled(state, FEATURES.onDemand) &&
    !isAnonymousListener(state) &&
    CapabilitiesUtils.isPlaybackEnabled(state)
  );
};

export const isAutoplayEnabled = (state: State): boolean => {
  const currentListener = getCurrentListenerFromState(state);
  return hasPremiumFeatures(state) && currentListener && currentListener.get('autoplayEnabled');
};

//  This is a heuristic that can return a false negative, but it's the best we can do without backend changes
// (e.g. if they've played a PA source incognito or in another browser Recently Played will be empty)
export const isNonPremiumListenerLikelyFTUX = (state: State): boolean => {
  const isCollectionEmpty = getInDomain(
    'collection.utils.getIsAllFilteredCollectionEmpty',
    true,
  )(state);
  const isRecentlyPlayedEmpty = getInDomain(
    'collection.utils.isRecentlyPlayedSPSEmpty',
    true,
  )(state);

  return (
    !hasPremiumFeatures(state) &&
    isCollectionEmpty &&
    isRecentlyPlayedEmpty &&
    !isAnonymousListener(state)
  );
};

function _buildListItemProps({ ids = [], stationId, disabled }: $TSFixMe): $TSFixMe {
  const listItemProps: $TSFixMe = [];
  ids &&
    // @ts-expect-error TS(7006) FIXME: Parameter 'id' implicitly has an 'any' type.
    ids.forEach(id => {
      listItemProps.push({ id, stationId, disabled });
    });
  return listItemProps;
}

function memoKey(feedbackIds: $TSFixMe, stationId: $TSFixMe): string | undefined {
  if (feedbackIds) {
    return feedbackIds.toString() + stationId;
  }
}

function _memoizeStationFeedbackProps(feedbackIds: $TSFixMe): $TSFixMeFunction {
  return memoize((stationId, disabled) => {
    return _buildListItemProps({ ids: feedbackIds, stationId, disabled });
  }, memoKey.bind(null, feedbackIds));
}

export const getStationFeedbackIds = (state: State, stationId: string): $TSFixMe => {
  return state.feedback.get(Constants.Feedback.Keys.FEEDBACK_IDS).get(stationId);
};

/**
 * Creates a list of feedback pandoraIds filtered by thumb type;
 * prevents blank rowItems from being rendered
 *
 * @param {object} state - store state
 * @param {string} stationId - string identifier for the station we went feedback from
 * @param {boolean} isPositive - boolean identifying which thumb type to render
 * @returns {object} a list of station feedback pandoraIds filtered by thumb type
 */
export const getStationFeedbackPandoraIds = (
  state: State,
  stationId: string,
  isPositive: boolean,
): $TSFixMe => {
  const feedbackIds = state.feedback
    .get(Constants.Feedback.Keys.FEEDBACK_IDS)
    .get(stationId, List());

  return feedbackIds.map((feedbackId: string) => {
    const feedback = getFeedbackByFeedbackId(state, feedbackId) || Map();
    const isSameFeedback = feedback.get('isPositive') === isPositive;
    if (feedback.size && isSameFeedback) {
      return feedback.get('pandoraId');
    }
  });
};

export const getStationThumbedTrackIds = (state: State, stationId: string): $TSFixMe => {
  const tracks =
    state.feedbackT3.getIn([stationId, Constants.Feedback.Keys.THUMBED_TRACKS]) || List();
  return tracks.map((track: $TSFixMe) => (track ? track.get('pandoraId', '') : ''));
};

export const createGetStationFeedbackPropsSelector = createSelector(
  getStationFeedbackIds,
  _memoizeStationFeedbackProps,
);

// these selector was created so the current StationDetails implementation isn't affected.
// one of the two should be removed after the feature flag is removed (WEBCA-299)
const emptyList = List();
export const createGetCollectedStationFeedbackPropsSelector = createSelector(
  // @ts-expect-error TS(2345) FIXME: Argument of type '((_state: any, _stationId: strin... Remove this comment to see the full error message
  [
    (state: State): $TSFixMe => getThumbedTracks(state) || emptyMap,
    (state: State, stationId: string): $TSFixMe =>
      getStationFeedbackIds(state, stationId) || emptyList,
    (_state: State, stationId: string): $TSFixMe => stationId,
    (state: State, stationId: string): $TSFixMe =>
      getInDomain('playback.utils.isOwnStation')(state, stationId),
    (_state: State, _stationId: string, isThumbsUp: boolean): $TSFixMe => isThumbsUp,
  ],
  (
    allThumbs: $TSFixMe,
    feedbackIds: $TSFixMe,
    stationId: $TSFixMe,
    isOwnStation: $TSFixMe,
    isThumbsUp: $TSFixMe,
  ) => {
    const feedbackProps: $TSFixMe = [];

    feedbackIds.forEach((feedbackId: $TSFixMe, key: $TSFixMe) => {
      const index = feedbackIds.keySeq().findIndex((i: $TSFixMe) => i === key);
      const feedback = allThumbs.get(feedbackId, emptyMap);
      const isSameFeedback = feedback.get('isPositive') === isThumbsUp;

      if (isSameFeedback) {
        feedbackProps.push({
          artistSeoToken: feedback.get('artistSeoToken'),
          feedbackId,
          id: feedback.get('pandoraId'),
          index,
          isNegativeFeedback: !isThumbsUp,
          isOwnStation,
          musicId: feedback.get('musicId'),
          sourceType: Constants.SourceTypes.SourceTypes.STATION_THUMB_UP,
          stationId,
        });
      }
    });

    return feedbackProps;
  },
);

export const _memoizeListItemProps = (ids: $TSFixMe): $TSFixMe => {
  return memoize(() => _buildListItemProps({ ids }));
};

export const getFeedbackIds = (state: State, webname: $TSFixMe): $TSFixMe => {
  return state.feedback.get(Constants.Feedback.Keys.FEEDBACK_IDS).get(webname);
};

export const createGetFeedbackPropsSelector = createSelector(getFeedbackIds, _memoizeListItemProps);

export const isHighQualityStreamingAvailable = (state: State): boolean => {
  return isFeatureEnabled(state, FEATURES.highQualityStreamingAvailable);
};

/**
 * Determines if the player should be requesting high quality audio from the server.
 *
 * @param {object} state - store state
 * @returns {boolean} should the player request high quality audio
 */
export const shouldUseHighQualityStreaming = (state: State): boolean => {
  const currentListener = getCurrentListenerFromState(state);

  if (currentListener) {
    // We want to drop bit rate if we are observing playback problems due to bandwidth
    const enoughBandwidth = !getInDomain(
      'playback.utils.getHasExperiencedBandwidthError',
      null,
    )(state);
    const canUseHiQuality = isHighQualityStreamingAvailable(state);
    const choseHighQuality = currentListener.get('highQualityStreamingEnabled');
    return enoughBandwidth && canUseHiQuality && choseHighQuality;
  }

  return false;
};

/**
 * Gets current user and determines what kind of audio file to request for them
 * This will be the format that we will attempt to use for non-live tracks.
 *
 * @param {object} state - store state
 * @returns {string} either mp3-hifi or aacplus depending on subscription tier
 */
export const getCurrentListenerDefaultAudioFormat = (
  state: State,
): ValueOf<typeof AudioFormats> => {
  const useHighQuality = shouldUseHighQualityStreaming(state);
  return useHighQuality ? AudioFormats.HIGHEST_QUALITY : AudioFormats.LOWEST_QUALITY;
};

// @ts-expect-error TS(6133) FIXME: 'state' is declared but its value is never read.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const isSxmEncryptedStationPreloadEnabled = (state: State): false => {
  return false; // We might re-run it in the near future.
};

// @ts-expect-error TS(6133) FIXME: 'state' is declared but its value is never read.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const isSXMEncryptedStationUnfrag = (state: State): false => {
  return false; // We might re-run it in the near future.
};

export const isSingleHarnessForStationAndAudioAdsEnabled = (state: State): boolean => {
  return (
    isExperimentEnabled(state, TREATMENT_ARMS.singleHarnessForStationAndAudioAdsEnabled) ||
    isExperimentEnabled(state, TREATMENT_ARMS.sxmEncryptedStationFragmented) ||
    isExperimentEnabled(state, TREATMENT_ARMS.sxmUnencryptedMediaelementPlayback2021) ||
    isExperimentEnabled(state, TREATMENT_ARMS.singleHarnessForUnencryptedStationAndAudioAdsEnabled)
  );
};

export const isSxmUnencryptedMediaelementPlayback2021Enabled = (state: State): boolean => {
  return isExperimentEnabled(state, TREATMENT_ARMS.sxmUnencryptedMediaelementPlayback2021);
};

export const isSxmUnencryptedWAAPlayback2021Enabled = (state: State): boolean => {
  return isExperimentEnabled(state, TREATMENT_ARMS.sxmUnencryptedWAAPlayback2021);
};

// @ts-expect-error TS(6133) FIXME: 'state' is declared but its value is never read.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const isPodcastsOnSxmPlayerEnabled = (state: State): false => {
  return false;
  // will flip this when we actually create the experiment
  // return isExperimentEnabled(state, TREATMENT_ARMS.podcastsOnSxmPlayerEnabled);
};

export const isSxmFetchDownloaderEnabled = (state: State): boolean => {
  return isExperimentEnabled(state, TREATMENT_ARMS.sxmFetchDownloader);
};

export const isSxmEncryptedStationFetchDownloaderEnabled = (state: State): boolean => {
  return isExperimentEnabled(state, TREATMENT_ARMS.sxmEncryptedStationFetchDownloader);
};

export const isSxmFetchDownloaderV2Enabled = (state: State): boolean => {
  return isExperimentEnabled(state, TREATMENT_ARMS.sxmFetchDownloaderV2);
};

export const isSxmEncryptedStationFragmentedEnabled = (state: State): boolean => {
  return isExperimentEnabled(state, TREATMENT_ARMS.sxmEncryptedStationFragmented);
};

export const isSxmEncryptedStationFetchDownloaderV2Enabled = (state: State): boolean => {
  return isExperimentEnabled(state, TREATMENT_ARMS.sxmEncryptedStationFetchDownloaderV2);
};

export const isSxmEncryptedStationFetchDownloaderV2MultipleHarnessEnabled = (
  state: State,
): boolean => {
  return isExperimentEnabled(
    state,
    TREATMENT_ARMS.sxmEncryptedStationFetchDownloaderV2MultipleHarness,
  );
};

export const isAPSPandoraStationEnabled = (state: State): boolean => {
  const isAllowT1Users = TunerVarUtils.allowT1UsersForApsStation(state);
  const isUserInT2Experiment = isExperimentEnabled(state, TREATMENT_ARMS.stationAPST2Migration);
  const isUserInT3Experiment = isExperimentEnabled(state, TREATMENT_ARMS.stationAPST3Migration);
  const isTier1User = getInDomain('payments.selectors.productTierIsAdSupported', false)(state);
  const isTier2User = getInDomain('payments.selectors.productTierIsPlus', false)(state);
  const isTier3User = getInDomain('payments.selectors.hasPremiumPlan', false)(state);

  if (isUserInT2Experiment && isUserInT3Experiment) {
    return isAllowT1Users ? true : isTier2User || isTier3User;
  } else if (isUserInT2Experiment) {
    return isAllowT1Users ? isTier1User || isTier2User : isTier2User;
  } else if (isUserInT3Experiment) {
    return isAllowT1Users ? isTier1User || isTier3User : isTier3User;
  }

  return false;
};

export const setDatadogClientUserAndMetaData = (state: State): void => {
  const listenerToken = getCurrentListenerTokenFromState(state);
  const listenerId = getCurrentListenerIdFromState(state);
  const listenerTier = getBranding(state);
  const isAnonymous = isAnonymousListener(state);
  const experiments = getUserExperimentsMap(state).toJS();

  const id = isAnonymous ? `anon${listenerId}` : listenerId;

  setDatadogUser(id, {
    tier: listenerTier,
    userId: listenerToken,
    isAnonymous,
  });

  setDatadogContext('experiments', experiments);

  if (isSxmEncryptedStationFetchDownloaderEnabled(state)) {
    setDatadogTag('FetchDownloader-11356', true);
  }
  if (isSxmEncryptedStationFetchDownloaderV2Enabled(state)) {
    setDatadogTag('FetchDownloaderV2-11947', true);
  }
  if (isSxmEncryptedStationFetchDownloaderV2MultipleHarnessEnabled(state)) {
    setDatadogTag('MultipleHarness-11637', true);
  }
  if (isAPSPandoraStationEnabled(state)) {
    setDatadogTag('APS-Pandora-Station-11999', true);
  }
};

export const getListenerSessionToken = (state: State): $TSFixMe => {
  return `${getCurrentListenerIdFromState(state)}:${state.usage.get(
    Constants.Usage.UsageKeys.SESSION_START_TIME,
  )}`;
};

function getUsage(state: State): $TSFixMe {
  return state.usage;
}

export const getTotalListeningTime = createSelector(getUsage, usage =>
  usage.get(Constants.Usage.UsageKeys.TOTAL_LISTENING_TIME),
);

export const getCurrentResourcePlaytimeStart = createSelector(getUsage, usage =>
  usage.get(Constants.Usage.UsageKeys.CURRENT_RESOURCE_PLAYTIME_START),
);

export const getListenerSessionStartTime = createSelector(getUsage, usage =>
  usage.get(Constants.Usage.UsageKeys.SESSION_START_TIME),
);

export const getTotalPlaytimeForCurrentTrackSeconds = createSelector(
  getTotalListeningTime,
  getCurrentResourcePlaytimeStart,
  (totalListeningTime, currentResourcePlaytimeStart) => {
    const seconds = (totalListeningTime - currentResourcePlaytimeStart) / 1000;
    return isNaN(seconds) ? 0 : seconds;
  },
);

export const getListenerSessionLength = createSelector(
  getListenerSessionStartTime,
  listenerSessionStartTime => {
    const seconds = (getNow() - listenerSessionStartTime) / 1000;
    return isNaN(seconds) ? 0 : seconds;
  },
);

export const hasExceededAnonListeningTime = createSelector(
  getTotalListeningTime,
  totalListeningTime => totalListeningTime > ANON_TIMEOUT,
);

/*
 * Checks state to see if we the current user is timed out
 * @returns {Boolean} is the current user timed out?
 */
export const isAnonTimedOut = createSelector(
  isAnonymousListener,
  hasExceededAnonListeningTime,
  hasLoggedIn,
  (isAnon, exceededAnonListeningTime, hasLoggedInPreviously) =>
    isAnon && (exceededAnonListeningTime || hasLoggedInPreviously),
);

/*
 * Checks to see if listener should potentially be shown smart conversion upsell coachmarks
 * @returns {Boolean} true if we should; false otherwise
 */
export const isListenerEligibleForSmartConversion = (state: State): $TSFixMe => {
  return (
    state.smartConversion.get(SmartConversionKeys.IS_SMART_CONVERSION_ENABLED_KEY) &&
    state.smartConversion.get(SmartConversionKeys.IS_QUALIFIED_FOR_UPSELL_KEY) &&
    TunerVarUtils.isUnitedStates(state)
  );
};

export const isCurrentListenerBingeSkipping = (state: State): $TSFixMe => {
  const listenerId = getCurrentListenerIdFromState(state);
  return state.entities.getIn(
    ['listeners', ListenerKeys.LISTENERS_KEY, listenerId, 'isBingeSkipping'],
    false,
  );
};

export const getListenerFromWebName = (state: State, webname: $TSFixMe): $TSFixMe => {
  const listeners = state.entities.getIn(['listeners', ListenerKeys.LISTENERS_KEY]);
  return listeners.find((listener: $TSFixMe) => {
    return listener.get('webname') === webname;
  });
};

export const isWebNameOfCurrentListener = (state: State, webname: $TSFixMe): $TSFixMe => {
  return getListenerFromWebName(state, webname).equals(getCurrentListenerFromState(state));
};

export const getCurrentListenerWebname = (state: State): $TSFixMe => {
  const listener = getCurrentListenerFromState(state);
  if (!listener) {
    return null;
  }
  return listener.get('webname');
};

export const isListenerAdSupportedSkip = (state: State): boolean => {
  return isFeatureEnabled(state, FEATURES.adSupportedSkip);
};

export const isCurrentListenerEligibleForPremium = (state: State): boolean => {
  const currentListener = getCurrentListenerFromState(state);
  return currentListener && currentListener.get(ListenerKeys.PREMIUM_ELIGIBILITY);
};

export const isListenerAdFreeSkip = (state: State): boolean => {
  return isFeatureEnabled(state, FEATURES.adFreeSkip);
};

export const isListenerFlexSkip = (state: State): boolean => {
  return isListenerAdSupportedSkip(state) || isListenerAdFreeSkip(state);
};

// https://wiki.savagebeast.com/pages/viewpage.action?pageId=63278975
// hasStationSkips only need to be passed if statsAction is SKIP_TAPPED
// TODO
export const getListenerRewardContextForStats = (
  state: State,
  statsAction: $TSFixMe,
  hasStationSkips = false,
): $TSFixMe => {
  if (isListenerAdFreeSkip(state)) {
    return getInDomain('rewards.constants.FlexRewardContextValues.T2_UNLIMITED');
  }
  const isReplayAction = statsAction === 'replayed_tapped';
  const isSkipAction = statsAction === 'skip_tapped';
  if (isReplayAction) {
    if (getInDomain('rewards.utils.canUseReplayReward')(state)) {
      return getInDomain('rewards.constants.FlexRewardContextValues.T1_REPLAY_YES_REWARD');
    }
    return getInDomain('rewards.constants.FlexRewardContextValues.T1_REPLAY_NO_REWARD');
  }
  if (isSkipAction) {
    if (getInDomain('rewards.utils.canUseSkipReward')(state)) {
      if (hasStationSkips) {
        return getInDomain(
          'rewards.constants.FlexRewardContextValues.T1_SKIPS_NOT_AT_LIMIT_YES_REWARD',
        );
      }
      return getInDomain('rewards.constants.FlexRewardContextValues.T1_SKIPS_AT_LIMIT_YES_REWARD');
    }
    if (hasStationSkips) {
      return getInDomain(
        'rewards.constants.FlexRewardContextValues.T1_SKIPS_NOT_AT_LIMIT_NO_REWARD',
      );
    }
    return getInDomain('rewards.constants.FlexRewardContextValues.T1_SKIPS_AT_LIMIT_NO_REWARD');
  }
  return null;
};

export const getCurrentListenerStationsThatHaveReceivedFragments = (state: State): $TSFixMe => {
  const currentListener = getCurrentListenerFromState(state);
  return currentListener.get('stationsThatHaveReceivedFragments');
};

export const hasSeenWebPremiumWelcome = (state: State): boolean => {
  return isFeatureEnabled(state, FEATURES.seenWebPremiumWelcome);
};

interface IUserSettingsFormValues {
  newBirthYear: string;
  newGender: string;
  newPassword: string;
  newUsername: string;
  newZipCode: string;
}

export const getUserSettingsFormValues = (state: State): IUserSettingsFormValues => {
  const values = {
    newBirthYear: '',
    newGender: '',
    newUsername: '',
    newZipCode: '',
    newPassword: '',
  };
  if (state.form && state.form.UserSettings_AccountInfo) {
    const formValues = getValues(state.form.UserSettings_AccountInfo);
    values.newUsername = formValues.username;
    values.newBirthYear = formValues.birthYear;
    values.newZipCode = formValues.zipCode;
    values.newGender = formValues.gender;

    // don't try to reset password unless new password is given
    if (formValues.password.length) {
      values.newPassword = formValues.password;
    }
  }
  return values;
};

export const shouldShowDisplayAds = (state: $TSFixMe): boolean => {
  return !isAnonymousListener(state) && listenerIsBasic(state);
};

export const getHashedListenerId = (state: State): string | null => {
  const listenerId = getCurrentListenerIdFromState(state);
  return listenerId ? hex_md5(listenerId) : null;
};

export const generateClientCorrelationId = (state: State): string => {
  const now = getNow();
  const listenerId = getCurrentListenerIdFromState(state);
  const randomNumber = Math.random();

  return hex_md5(`${listenerId}${now}${randomNumber}`);
};

export const areVideoAdsEnabledForListener = (state: State): boolean => {
  return (
    listenerIsBasic(state) && !isAnonymousListener(state) && !CapabilitiesUtils.isMobileWeb(state)
  );
};

export const isAuthenticatedListener = (state: State): boolean => {
  return isLoggedIn(state) && !isAnonymousListener(state);
};

export const getLoginRedirectOnSuccess = (state: State): $TSFixMe => {
  return state.login.get(LoginKeys.LOGIN_REDIRECT_ON_SUCCESS_KEY);
};

export const isDetectorTrackMetadataEnabled = (state: State): boolean => {
  return isExperimentEnabled(state, TREATMENT_ARMS.detectorTrackMetadata);
};

export const isAudioAdsSkippabilityEnabled = (state: State): boolean => {
  return isExperimentEnabled(state, TREATMENT_ARMS.audioAdsSkippabilityEnabled);
};

export const isWebPingRadioHealthEnabled = (state: State): boolean => {
  return isExperimentEnabled(state, TREATMENT_ARMS.webPingRadioHealth);
};

export const isMeasurementThroughOmsdkExperimentEnabled = (state: State): boolean => {
  return isExperimentEnabled(state, TREATMENT_ARMS.measurementThroughOmsdk);
};

export const isListenerInAPSMM4Experiment = (state: State): boolean => {
  return isExperimentEnabled(state, TREATMENT_ARMS.webAPSMigrationMM4);
};

export const isListenerInDebugWebca2503Experiment = (state: State): boolean => {
  return isExperimentEnabled(state, TREATMENT_ARMS.debug_webca_2503_treatment);
};

export const isListenerInUID2Experiment = (state: State): boolean => {
  if (isListenerInDebugWebca2503Experiment(state)) {
    return false;
  }

  return isExperimentEnabled(state, TREATMENT_ARMS.web_ads_uid2);
};

export const isListenerInWebCancelFlowExperiment = (state: State): boolean => {
  return isExperimentEnabled(state, TREATMENT_ARMS.web_cancel_flow);
};

export const isListenerInWebPaymentPageUpdatesExperiment = (state: State): boolean => {
  return isExperimentEnabled(state, TREATMENT_ARMS.web_payment_page_updates);
};

export const isListenerInWebSwitchPlansModalExperiment = (state: State): boolean => {
  return isExperimentEnabled(state, TREATMENT_ARMS.web_switch_plans_modal_updates);
};
